/**
 * Typography Design Tokens
 *
 * Typography-specific token handling that uses the general token utilities.
 * This matches the SCSS core/typography/_tokens.scss implementation.
 */

import { createTokenExtractor, designTokenValue, type TokenExtractor } from '../core/tokens';
import { CssValue } from '../core/types';
import { type TypographyProperty, type TypographyVariantName, type TypographyWeightName } from '../values/typography';

/**
 * High-order function for typography tokens that includes property name with type safety
 *
 * Matches the SCSS typography API (e.g., font-family, font-size)
 *
 * @param property - The typography property
 * @returns A function that extracts typography variables by variant name (type-safe)
 */
export function createTypographyTokenExtractor(property: TypographyProperty): TokenExtractor<TypographyVariantName> {
    return function (variant: TypographyVariantName, fallback?: string): CssValue {
        const tokenName = `${variant}-${property}`;

        return designTokenValue('typography', tokenName, fallback);
    };
}

/**
 * Typography token extractors with type safety
 *
 * Each function matches the corresponding SCSS function and only accepts valid variant names
 */
export const fontFamily = createTypographyTokenExtractor('font-family');
export const fontSize = createTypographyTokenExtractor('font-size');
export const fontWeight = createTypographyTokenExtractor('font-weight');
export const fontStyle = createTypographyTokenExtractor('font-style');
export const lineHeight = createTypographyTokenExtractor('line-height');
export const letterSpacing = createTypographyTokenExtractor('letter-spacing');
export const textTransform = createTypographyTokenExtractor('text-transform');
export const textDecoration = createTypographyTokenExtractor('text-decoration');

/**
 * Typography weight token extractor with type safety
 *
 * Matches SCSS: weight($variant)
 */
export const weight = createTokenExtractor<TypographyWeightName>('typography-weight');

/**
 * Example usage:
 *
 * ```typescript
 * import { fontFamily, fontSize, fontWeight, lineHeight, weight } from './typography';
 *
 * const headingFont = fontFamily('heading-1'); // ✅ Valid variant name
 * const headingSize = fontSize('heading-2'); // ✅ Valid variant name
 * const headingWeight = fontWeight('paragraph-1'); // ✅ Valid variant name
 * const headingLine = lineHeight('caption-1'); // ✅ Valid variant name
 * const boldWeight = weight('bold'); // ✅ Valid weight name
 *
 * // TypeScript will show error for invalid names:
 * // const invalidFont = fontFamily('invalid-variant'); // ❌ Type error
 * // const invalidWeight = weight('invalid-weight'); // ❌ Type error
 * ```
 */
